import React, { Fragment, useEffect, useRef } from 'react';
import BaseFilter, { BaseFilterParams } from './BaseFilter';
import { ArrowRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import ButtonV2 from '../../button/ButtonV2';
import { useFilterContext } from '../FilterV2Context';
import { TextFilterCondition } from '../filterV2.types';
import { getTextConditionLabel } from '../utils';

const INPUT_NAME = 'text-filter-input';

export type TextFilterProps = BaseFilterParams;

/**
 * Filter option that contains various text conditions.
 *
 * *Use Case*: Filtering a string of text with essentially a search term.
 *
 * *Example*: Account report code begins with "ASS" would match all asset accounts.
 *
 * @param props TextFilterProps
 */
function TextFilter({ id, label }: TextFilterProps): JSX.Element {
    const ctx = useFilterContext();
    const inputRefs = useRef<HTMLInputElement[]>([]);

    const { condition, values } = ctx.getDraft(id, 'text');

    const activeValues = values.filter((v) => v.trim());

    useEffect(() => {
        if (values.length === 0) {
            ctx.updateValues('text', id, { values: [''] });
        }
    }, [ctx, id, values.length]);

    function handleChange(index: number, value: string): void {
        const copy = [...values];
        copy[index] = value;
        ctx.updateValues('text', id, { values: copy });
    }

    function handleConditionChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        ctx.updateValues('text', id, { condition: e.target.value as TextFilterCondition });
        inputRefs.current[0]?.focus();
    }

    function addInput(): void {
        const copy = [...values, ''];
        ctx.updateValues('text', id, { values: copy });
    }

    function handleInputRemove(index: number): void {
        if (index === 0) {
            return;
        }

        const copy = [...values];
        copy.splice(index, 1);

        inputRefs.current.splice(index, 1);

        setTimeout(() => {
            inputRefs.current?.at(-1)?.focus();
        }, 1);

        ctx.updateValues('text', id, { values: copy });
    }

    return (
        <BaseFilter id={id} label={label} activeCount={activeValues.length} key={id} onMenuClose={console.log}>
            <div className="grid items-center grid-cols-[125px_20px_100px_20px] gap-2 gap-y-2">
                {/* Condition + First Value */}
                <select
                    className="bg-white dark:bg-white/[0.12] text-xs  rounded border-gray-300 dark:border-white/[0.05] py-1 px-2 outline-none focus:ring-0 cursor-pointer overflow-hidden w-[125px] text-gray-700 dark:text-gray-200"
                    defaultValue={condition}
                    onChange={handleConditionChange}
                >
                    <option value="eq">{getTextConditionLabel('eq')}</option>
                    <option value="ne">{getTextConditionLabel('ne')}</option>
                    <option value="bw">{getTextConditionLabel('bw')}</option>
                    <option value="ew">{getTextConditionLabel('ew')}</option>
                    <option value="con">{getTextConditionLabel('con')}</option>
                    <option value="nco">{getTextConditionLabel('nco')}</option>
                </select>
                <ArrowRightIcon className="h-5 w-5 text-indigo-600" />
                <input
                    ref={(e): void => {
                        inputRefs.current[0] = e;
                    }}
                    name={INPUT_NAME}
                    autoFocus
                    type="text"
                    value={values[0] || ''}
                    onChange={(e): void => handleChange(0, e.target.value)}
                    onKeyDown={(e): void => {
                        if (e.key === 'Enter') {
                            addInput();
                        }
                    }}
                    className="col-span-2 outline-none text-xs text-gray-700 rounded border border-gray-200 py-1 px-2 w-full focus:ring-indigo-600"
                />
                {[...values].slice(1).map((_, i) => (
                    <Fragment key={i}>
                        <div></div>
                        <label className="text-sm text-gray-700 text-center select-none">or</label>
                        <input
                            name={INPUT_NAME}
                            autoFocus
                            ref={(e): void => {
                                inputRefs.current[i] = e;
                            }}
                            type="text"
                            className="outline-none text-xs text-gray-700 rounded border border-gray-200 py-1 px-2 w-[100px] focus:ring-indigo-600"
                            value={values[i + 1]}
                            onChange={(e): void => handleChange(i + 1, e.target.value)}
                            onKeyDown={(e): void => {
                                if (e.key === 'Enter') {
                                    addInput();
                                }
                                if (e.key === 'Backspace' && !values[i + 1]) {
                                    handleInputRemove(i + 1);
                                }
                            }}
                        />
                        <button
                            className="text-indigo-600 dark:text-indigo-400 h-4 w-4"
                            onClick={(): void => handleInputRemove(i + 1)}
                            tabIndex={-1}
                        >
                            <XMarkIcon />
                        </button>
                    </Fragment>
                ))}

                {/* Add value/condition button */}
                <div className="col-span-4 ml-auto">
                    <ButtonV2
                        label="Add condition"
                        leftIcon={<PlusCircleIcon className="stroke-2" />}
                        size="sm"
                        type="hollow"
                        onClick={(): void => {
                            addInput();
                        }}
                    />
                </div>
            </div>
        </BaseFilter>
    );
}

export default TextFilter;
